import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import type { Family, Cashout as CashoutType } from "@starquest/core";
import styles from "./Cashout.module.css";
import PreviewBanner from "../components/PreviewBanner";
import ThemeWrapper from "../components/ThemeWrapper";
import { useTheme } from "../hooks/useTheme";

export default function Cashout() {
  const navigate = useNavigate();
  const { familyId: authFamilyId } = useAuthStore();
  const { selectedKid, family, cashouts, setFamily, setCashouts } = useFamilyStore();
  const familyId = authFamilyId || selectedKid?.familyId || null;

  const [starsToRedeem, setStarsToRedeem] = useState(50);
  const [requesting, setRequesting] = useState(false);
  const [error,      setError]      = useState("");
  const [done,       setDone]       = useState(false);

  const kid    = selectedKid;
  const theme  = useTheme(selectedKid);
  const rate   = family?.settings.pointRate ?? 10;
  const minCO  = family?.settings.minCashout ?? 50;
  const maxPoints = kid?.pointBalance ?? 0;
  const dollars  = parseFloat((starsToRedeem / rate).toFixed(2));

  useEffect(() => {
    if (!selectedKid) navigate("/kid");
  }, [selectedKid, navigate]);

  useEffect(() => {
    if (!familyId) return;
    api.getFamily(familyId).then((f) => setFamily(f as Family));
    if (kid) api.listCashouts(familyId).then((c) => setCashouts(c as CashoutType[]));
  }, [familyId, kid?.kidId]);

  const kidCashouts = cashouts.filter((c) => c.kidId === kid?.kidId);
  const pendingCount = kidCashouts.filter((c) => c.status === "pending").length;

  async function handleRequest() {
    if (!familyId || !kid) return;
    if (starsToRedeem < minCO) { setError(`Minimum cashout is ${minCO} ${theme.pointNamePlural.toLowerCase()}`); return; }
    if (starsToRedeem > maxPoints) { setError(`Not enough ${theme.pointNamePlural.toLowerCase()}`); return; }
    setError(""); setRequesting(true);
    try {
      await api.requestCashout(familyId, { kidId: kid.kidId, points: starsToRedeem });
      setDone(true);
      api.listCashouts(familyId).then((c) => setCashouts(c as CashoutType[]));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally { setRequesting(false); }
  }

  if (!kid) return (
    <div className={styles.page}>
      <PreviewBanner />
      <div className={styles.starsBg} aria-hidden />
      <div className={styles.noKidState}>
        <div className={styles.noKidIcon}>⭐</div>
        <div className={styles.noKidTitle}>{`Pick a ${theme.kidRole.toLowerCase()} first`}</div>
        <p className={styles.noKidDesc}>Go back to the home screen and tap your name to get started.</p>
        <button type="button" className={styles.noKidBtn} onClick={() => navigate("/kid")}>← Back to Home</button>
      </div>
    </div>
  );

  return (
    <ThemeWrapper kid={selectedKid}>
    <div className={styles.page}>
      <PreviewBanner />
      <div className={styles.starsBg} aria-hidden />

      <div className={styles.head}>
        <button className={styles.back} onClick={() => navigate("/kid")}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18 L9 12 L15 6"/></svg>
        </button>
        <div>
          <h1 className={styles.title}>{theme.pointBankTitle}</h1>
          <div className={styles.subtitle}>Convert your {theme.pointNamePlural.toLowerCase()} to real rewards</div>
        </div>
        <div className={styles.pill}>{`${theme.pointEmoji} ${kid.pointBalance}`} available</div>
      </div>

      {done ? (
        <div className={styles.doneMsg}>
          <div className={styles.doneIcon}>🎉</div>
          <div className={styles.doneTitle}>Request sent!</div>
          <p className={styles.doneSub}>Your {theme.parentRole} will approve and pay you ${dollars}.</p>
          <button type="button" className={styles.doneBtn} onClick={() => { setDone(false); navigate("/kid"); }}>Back to Home</button>
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.left}>
            {/* Balance display */}
            <div className={styles.bankCard}>
              <div className={styles.bankLabel}>Your {theme.pointName.toLowerCase()} balance</div>
              <div className={styles.bankBal}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="var(--gold)"><path d="M12 2 L14.6 9.4 L22 10 L16.3 14.8 L18.2 22 L12 17.8 L5.8 22 L7.7 14.8 L2 10 L9.4 9.4 Z"/></svg>
                {kid.pointBalance}
              </div>
              <div className={styles.equivBox}>
                <span className={styles.equivDollar}>${(kid.pointBalance / rate).toFixed(2)}</span>
                <span className={styles.equivRate}>{rate} {theme.pointEmoji} = $1</span>
              </div>

              <div className={styles.rateInfo}>
                <span><b>{rate} {theme.pointEmoji}</b> = <em>$1</em></span>
                <span className={styles.rateWho}>Set by {theme.parentRole}</span>
              </div>

              {maxPoints === 0 ? (
                <div className={styles.zeroState}>
                  <div className={styles.zeroIcon}>⭐</div>
                  <div className={styles.zeroTitle}>No {theme.pointNamePlural.toLowerCase()} yet!</div>
                  <div className={styles.zeroSub}>
                    Complete {theme.challengeNamePlural.toLowerCase()} to earn your first {theme.pointNamePlural.toLowerCase()}, then come back here to cash out.
                  </div>
                </div>
              ) : (
                <>
                  {/* Slider */}
                  <div className={styles.sliderLabel}>
                    <span>{theme.pointNamePlural} to cash out</span>
                    <span className={styles.sliderAmt}>{theme.pointEmoji} {starsToRedeem}</span>
                  </div>
                  <input
                    type="range" className={styles.slider}
                    min={minCO} max={Math.max(maxPoints, minCO)} value={starsToRedeem}
                    onChange={(e) => setStarsToRedeem(+e.target.value)}
                  />
                  <div className={styles.quick}>
                    {[minCO, Math.round(maxPoints * 0.25), Math.round(maxPoints * 0.5), maxPoints]
                      .filter((v, i, a) => v > 0 && a.indexOf(v) === i)
                      .map((v) => (
                        <button key={v} type="button" className={`${styles.qBtn} ${starsToRedeem === v ? styles.qActive : ""}`}
                          onClick={() => setStarsToRedeem(v)}>
                          {theme.pointEmoji}{v}
                        </button>
                      ))}
                  </div>
                  <div className={styles.receipt}>
                    <div className={styles.receiptTitle}>Summary</div>
                    <div className={styles.receiptRow}><span>{theme.pointNamePlural} used</span><span>{theme.pointEmoji} {starsToRedeem}</span></div>
                    <div className={styles.receiptRow}><span>Rate</span><span>{rate} {theme.pointEmoji} = $1</span></div>
                    <div className={styles.receiptRow}><span>Remaining</span><span>{theme.pointEmoji} {Math.max(0, maxPoints - starsToRedeem)}</span></div>
                    <div className={`${styles.receiptRow} ${styles.receiptTotal}`}>
                      <span>You receive</span>
                      <span className={styles.receiptGreen}>${dollars}</span>
                    </div>
                  </div>
                  {error && <p className={styles.error}>{error}</p>}
                  <button type="button" className={styles.reqBtn} onClick={handleRequest} disabled={requesting || starsToRedeem > maxPoints || starsToRedeem < minCO}>
                    {requesting ? "Requesting…" : `💸 Request $${dollars}`}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={styles.right}>
            {/* History */}
            <div className={styles.histCard}>
              <h3 className={styles.histTitle}>Cashout History <span className={styles.histCount}>{pendingCount} pending</span></h3>
              {kidCashouts.length === 0 && <p className={styles.histEmpty}>No cashouts yet</p>}
              {kidCashouts.slice(0, 6).map((co) => (
                <div key={co.cashoutId} className={`${styles.histRow} ${co.status === "pending" ? styles.histPending : ""}`}>
                  <div className={styles.histIcon}>{co.status === "approved" ? "✅" : co.status === "declined" ? "❌" : "⏳"}</div>
                  <div className={styles.histMid}>
                    <div className={styles.histT1}>${co.dollars} cashout</div>
                    <div className={styles.histT2}>{co.status.toUpperCase()} · {new Date(co.requestedAt).toLocaleDateString()}</div>
                  </div>
                  <div className={styles.histAmt}>
                    <div className={styles.histDollars}>${co.dollars}</div>
                    <div className={styles.histStars}>{theme.pointEmoji} {co.points}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </ThemeWrapper>
  );
}
